import {
  AddressDisplay,
  cn,
  CopyButton,
  LiveIndicator,
  Spinner,
  StatCard,
  Table,
  useCopyToClipboard,
} from "@gotts.ai/ui";
import {
  ArrowLeftRight,
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Copy,
  Droplets,
  FileText,
  Fingerprint,
  Layers,
  LayoutDashboard,
  Zap,
} from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { decodeFunctionResult, encodeFunctionData } from "viem";

import { AnimatedBalance } from "../components/AnimatedBalance";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { HexConnector } from "../components/HexConnector";
import { PcbCard } from "../components/PcbCard";
import { ProtocolBadge } from "../components/ProtocolBadge";
import { useDeployment, useWallet } from "../context";
import { useBlockNumber } from "../hooks/useBlockNumber";
import { useRpcRequest } from "../hooks/useRpcRequest";
import {
  STATE_VIEW_ABI,
  V2_PAIR_ABI,
  V3_FACTORY_ABI,
  V3_POOL_ABI,
} from "../lib/abis";
import { DEVENV_TOKEN_PRICES, SEEDED_AGENTS } from "../lib/constants";
import { formatUsd } from "../lib/format";

import {
  computePairAddress,
  computePoolId,
  computeTvlUsd,
  getDecimals,
  V2_POOLS,
  V3_POOLS,
  V4_POOLS,
} from "./pool-explorer/types";

import type { Address } from "viem";

// ---------------------------------------------------------------------------
// Static data: contract display names
// ---------------------------------------------------------------------------

const CONTRACT_DISPLAY_NAMES: Record<
  string,
  { display: string; description: string; isContract?: boolean }
> = {
  // Foundation
  weth9: {
    display: "WETH9 (Wrapped ETH)",
    description: "ERC-20 wrapper for native ETH",
  },
  permit2: {
    display: "Permit2 (Token Approvals)",
    description: "Gasless token approval protocol",
  },
  // V2
  factory: { display: "Factory", description: "Deploys pair contracts" },
  router02: {
    display: "Router02",
    description: "Swap and liquidity routing for V2",
  },
  initCodePairHash: {
    display: "Init Code Pair Hash",
    description: "CREATE2 salt for pair address derivation",
    isContract: false,
  },
  // V3
  router: { display: "Swap Router", description: "Single-hop swaps" },
  quoterV2: {
    display: "QuoterV2",
    description: "Gas-free swap quotes for V3",
  },
  nonfungiblePositionManager: {
    display: "NFT Position Manager",
    description: "Manages V3 LP position NFTs",
  },
  nonfungibleTokenPositionDescriptor: {
    display: "NFT Descriptor",
    description: "On-chain SVG metadata for position NFTs",
  },
  tokenDescriptor: {
    display: "Token Descriptor",
    description: "Token symbol and decimal resolution",
  },
  tickLens: { display: "TickLens", description: "Batch tick data reader" },
  migrator: {
    display: "Migrator",
    description: "V2 to V3 liquidity migration",
  },
  staker: {
    display: "Staker",
    description: "Liquidity mining reward distribution",
  },
  swapRouter02: {
    display: "SwapRouter02",
    description: "Multi-hop swap router for V3",
  },
  owner: {
    display: "Owner",
    description: "V3 factory owner address",
    isContract: false,
  },
  // V4
  poolManager: {
    display: "PoolManager",
    description: "V4 singleton pool manager",
  },
  positionDescriptor: {
    display: "Position Descriptor",
    description: "On-chain metadata for V4 positions",
  },
  positionManager: {
    display: "Position Manager",
    description: "Manages V4 LP positions",
  },
  quoter: { display: "Quoter", description: "Gas-free swap quotes for V4" },
  stateView: {
    display: "StateView",
    description: "Read-only pool state accessor",
  },
  poolModifyLiquidityTest: {
    display: "ModifyLiquidityTest",
    description: "Test router for V4 liquidity ops",
  },
  poolSwapTest: {
    display: "PoolSwapTest",
    description: "Test router for V4 swaps",
  },
  // UniswapX
  exclusiveDutchOrderReactor: {
    display: "Exclusive Dutch Reactor",
    description: "Fills exclusive Dutch auction orders",
  },
  priorityOrderReactor: {
    display: "Priority Order Reactor",
    description: "Priority-fee based order execution",
  },
  v2DutchOrderReactor: {
    display: "V2 Dutch Reactor",
    description: "V2 Dutch auction order reactor",
  },
  orderQuoter: {
    display: "Order Quoter",
    description: "Off-chain order validation and quoting",
  },
  // ERC-8004
  identityRegistry: {
    display: "Identity Registry",
    description: "ERC-8004 agent identity NFTs",
  },
  reputationRegistry: {
    display: "Reputation Registry",
    description: "On-chain agent reputation scores",
  },
  // Vault
  identityAdapter: {
    display: "Identity Adapter",
    description: "ERC-8004 registry decoupling layer for vaults",
  },
  feeModule: {
    display: "Fee Module",
    description: "Management and performance fee accounting",
  },
  vaultImplementation: {
    display: "Vault Implementation",
    description: "ERC-4626 vault reference implementation",
  },
  vaultFactory: {
    display: "Vault Factory",
    description: "CREATE2 vault deployment and registry",
  },
};

// ---------------------------------------------------------------------------
// Static data: section config
// ---------------------------------------------------------------------------

interface SectionStyle {
  color: string;
  icon: typeof Droplets;
  protocol?: string;
}

const SECTION_CONFIG: Record<string, SectionStyle> = {
  Foundation: { color: "#C9A84C", icon: LayoutDashboard, protocol: "CORE" },
  "Uniswap V2": { color: "#26A17B", icon: Droplets, protocol: "V2" },
  "Uniswap V3": { color: "#4a7fb5", icon: Droplets, protocol: "V3" },
  "Uniswap V4": { color: "#8B5CF6", icon: Droplets, protocol: "V4" },
  "Universal Router": {
    color: "#4a7fb5",
    icon: ArrowLeftRight,
    protocol: "ROUTER",
  },
  UniswapX: { color: "#ffab40", icon: Zap, protocol: "UniswapX" },
  "ERC-8004": { color: "#FF9800", icon: Fingerprint, protocol: "ERC-8004" },
  Vaults: { color: "#10b981", icon: Building2, protocol: "VAULT" },
};

// ---------------------------------------------------------------------------
// Static data: navigation cards
// ---------------------------------------------------------------------------

interface NavCardDef {
  id: string;
  label: string;
  path: string;
  icon: typeof Droplets;
  description: string;
}

const NAV_CARDS: NavCardDef[] = [
  {
    id: "pools",
    label: "Pool Explorer",
    path: "/pools",
    icon: Droplets,
    description: "Browse V2, V3, V4 pools with live state",
  },
  {
    id: "swap",
    label: "Swap",
    path: "/swap",
    icon: ArrowLeftRight,
    description: "Execute token swaps across protocols",
  },
  {
    id: "liquidity",
    label: "Liquidity",
    path: "/liquidity",
    icon: Layers,
    description: "Add and manage LP positions",
  },
  {
    id: "uniswapx",
    label: "UniswapX Orders",
    path: "/uniswapx",
    icon: Zap,
    description: "Create and fill Dutch auction orders",
  },
  {
    id: "vaults",
    label: "Vault Explorer",
    path: "/vaults",
    icon: Building2,
    description: "Browse and manage ERC-4626 agent vaults",
  },
  {
    id: "agents",
    label: "Agent Identity",
    path: "/agents",
    icon: Fingerprint,
    description: "Register and manage ERC-8004 agents",
  },
];

// ---------------------------------------------------------------------------
// Gas color helper
// ---------------------------------------------------------------------------

function getGasColorClass(gwei: number): string {
  if (gwei <= 1) return "gas-default";
  if (gwei <= 50) return "gas-elevated";
  return "gas-high";
}

// ---------------------------------------------------------------------------
// CopyableAddress helper
// ---------------------------------------------------------------------------

function CopyableAddress({ address }: { address: string }) {
  const { copiedKey, copy } = useCopyToClipboard();
  return (
    <AddressDisplay
      address={address}
      leadChars={6}
      tailChars={4}
      fontSize={12}
      copyable
      copied={copiedKey === address}
      onCopy={() => {
        void copy(address, address);
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// KvRow — NieR-style key-value row with dotted separator
// ---------------------------------------------------------------------------

function KvRow({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: ReactNode;
  valueColor?: string;
}) {
  return (
    <div className="flex items-center font-mono text-[11px]">
      <span style={{ color: "var(--color-text-muted)" }}>{label}</span>
      <span
        className="mx-2 flex-1"
        style={{
          borderBottom: "1px dotted rgba(201, 168, 76, 0.12)",
        }}
      />
      <span style={{ color: valueColor ?? "var(--color-text-dim)" }}>
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ProtocolOverview() {
  const { deployment, loading } = useDeployment();
  const { data: blockNumber } = useBlockNumber();
  const { copiedKey, copy } = useCopyToClipboard();
  const { publicClient } = useWallet();
  const { request } = useRpcRequest();
  const navigate = useNavigate();
  const [gasPrice, setGasPrice] = useState<bigint | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({});

  // Pool state for metrics
  const [v2Reserves, setV2Reserves] = useState<
    Record<string, { reserve0: bigint; reserve1: bigint }>
  >({});
  const [v3Liquidity, setV3Liquidity] = useState<Record<string, bigint>>({});
  const [v4Liquidity, setV4Liquidity] = useState<Record<string, bigint>>({});

  // Gas price fetch
  useEffect(() => {
    let cancelled = false;
    async function fetchGas() {
      try {
        const price = await publicClient.getGasPrice();
        if (!cancelled) setGasPrice(price);
      } catch {
        // best-effort
      }
    }
    void fetchGas();
    return () => {
      cancelled = true;
    };
  }, [publicClient, blockNumber]);

  // -------------------------------------------------------------------------
  // readContract helper (mirrors PoolExplorer pattern)
  // -------------------------------------------------------------------------

  const readContract = useCallback(
    async (
      address: Address,
      abi: readonly unknown[],
      functionName: string,
      args: unknown[] = [],
    ) => {
      const data = encodeFunctionData({
        abi: abi as Parameters<typeof encodeFunctionData>[0]["abi"],
        functionName,
        args,
      });
      const result = await request("eth_call", [
        { to: address, data },
        "latest",
      ]);
      if (!result || result === "0x") return null;
      return decodeFunctionResult({
        abi: abi as Parameters<typeof decodeFunctionResult>[0]["abi"],
        functionName,
        data: result as `0x${string}`,
      });
    },
    [request],
  );

  // -------------------------------------------------------------------------
  // Token address helpers
  // -------------------------------------------------------------------------

  function getTokenAddress(symbol: string): Address | null {
    if (!deployment?.tokens) return null;
    const tokens = deployment.tokens as unknown as Record<string, string>;
    const addr = tokens[symbol];
    return addr ? (addr as Address) : null;
  }

  function sortAddresses(a: Address, b: Address): [Address, Address] {
    return a.toLowerCase() < b.toLowerCase() ? [a, b] : [b, a];
  }

  // -------------------------------------------------------------------------
  // V2 reserves fetch
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!deployment?.v2 || !deployment.tokens) return;
    const v2 = deployment.v2;

    async function fetchV2() {
      const states: Record<string, { reserve0: bigint; reserve1: bigint }> = {};

      for (const pool of V2_POOLS) {
        const tokenA = getTokenAddress(pool.tokens[0]);
        const tokenB = getTokenAddress(pool.tokens[1]);
        if (!tokenA || !tokenB) continue;

        const [token0, token1] = sortAddresses(tokenA, tokenB);
        const pairAddress = computePairAddress(
          v2.factory,
          token0,
          token1,
          v2.initCodePairHash,
        );

        try {
          const result = await readContract(
            pairAddress,
            V2_PAIR_ABI,
            "getReserves",
          );
          if (result) {
            const [r0, r1] = result as [bigint, bigint, number];
            const isTokenAToken0 =
              tokenA.toLowerCase() === token0.toLowerCase();
            states[pool.pair] = {
              reserve0: isTokenAToken0 ? r0 : r1,
              reserve1: isTokenAToken0 ? r1 : r0,
            };
          }
        } catch {
          // Pool may not exist
        }
      }

      setV2Reserves(states);
    }

    void fetchV2();
  }, [blockNumber, deployment?.v2.factory]);

  // -------------------------------------------------------------------------
  // V3 liquidity fetch
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!deployment?.v3 || !deployment.tokens) return;
    const v3 = deployment.v3;

    async function fetchV3() {
      const states: Record<string, bigint> = {};

      for (const pool of V3_POOLS) {
        const tokenA = getTokenAddress(pool.tokens[0]);
        const tokenB = getTokenAddress(pool.tokens[1]);
        if (!tokenA || !tokenB) continue;

        try {
          const poolAddr = await readContract(
            v3.factory,
            V3_FACTORY_ABI,
            "getPool",
            [tokenA, tokenB, pool.feeTier],
          );
          if (
            !poolAddr ||
            poolAddr === "0x0000000000000000000000000000000000000000"
          )
            continue;

          const liqResult = await readContract(
            poolAddr as Address,
            V3_POOL_ABI,
            "liquidity",
          );
          const key = `${pool.pair}-${String(pool.feeTier)}`;
          states[key] =
            liqResult !== null && liqResult !== undefined
              ? (liqResult as bigint)
              : 0n;
        } catch {
          // Pool may not exist
        }
      }

      setV3Liquidity(states);
    }

    void fetchV3();
  }, [blockNumber, deployment?.v3.factory]);

  // -------------------------------------------------------------------------
  // V4 liquidity fetch
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!deployment?.v4 || !deployment.tokens) return;
    const v4 = deployment.v4;

    async function fetchV4() {
      const states: Record<string, bigint> = {};
      const ZERO_ADDRESS =
        "0x0000000000000000000000000000000000000000" as Address;

      for (const pool of V4_POOLS) {
        const tokenA = getTokenAddress(pool.tokens[0]);
        const tokenB = getTokenAddress(pool.tokens[1]);
        if (!tokenA || !tokenB) continue;

        const [currency0, currency1] = sortAddresses(tokenA, tokenB);
        const poolId = computePoolId(
          currency0,
          currency1,
          pool.tickSpacing * 100,
          pool.tickSpacing,
          ZERO_ADDRESS,
        );

        try {
          const liqResult = await readContract(
            v4.stateView,
            STATE_VIEW_ABI,
            "getLiquidity",
            [poolId],
          );
          const key = `v4-${pool.pair}`;
          states[key] =
            liqResult !== null && liqResult !== undefined
              ? (liqResult as bigint)
              : 0n;
        } catch {
          // Pool may not exist
        }
      }

      setV4Liquidity(states);
    }

    void fetchV4();
  }, [blockNumber, deployment?.v4.stateView]);

  // -------------------------------------------------------------------------
  // Computed metrics
  // -------------------------------------------------------------------------

  const totalTvl = useMemo(() => {
    let sum = 0;
    for (const pool of V2_POOLS) {
      const state = v2Reserves[pool.pair];
      if (!state) continue;
      const d0 = getDecimals(pool.tokens[0]);
      const d1 = getDecimals(pool.tokens[1]);
      sum += computeTvlUsd(
        [
          { symbol: pool.tokens[0], amount: Number(state.reserve0) / 10 ** d0 },
          { symbol: pool.tokens[1], amount: Number(state.reserve1) / 10 ** d1 },
        ],
        DEVENV_TOKEN_PRICES,
      );
    }
    return sum;
  }, [v2Reserves]);

  const activePools = useMemo(() => {
    let count = 0;
    for (const pool of V2_POOLS) {
      const s = v2Reserves[pool.pair];
      if (s && (s.reserve0 > 0n || s.reserve1 > 0n)) count++;
    }
    for (const pool of V3_POOLS) {
      const liq = v3Liquidity[`${pool.pair}-${String(pool.feeTier)}`];
      if (liq !== undefined && liq > 0n) count++;
    }
    for (const pool of V4_POOLS) {
      const liq = v4Liquidity[`v4-${pool.pair}`];
      if (liq !== undefined && liq > 0n) count++;
    }
    return count;
  }, [v2Reserves, v3Liquidity, v4Liquidity]);

  const totalPools = V2_POOLS.length + V3_POOLS.length + V4_POOLS.length;
  const agentCount = SEEDED_AGENTS.length;

  const tierBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const agent of SEEDED_AGENTS) {
      counts[agent.tier] = (counts[agent.tier] ?? 0) + 1;
    }
    // Return format for highest tier
    const tiers = ["sovereign", "trusted", "verified", "basic", "unverified"];
    for (const tier of tiers) {
      if (counts[tier]) {
        return `${String(counts[tier])} ${tier}`;
      }
    }
    return "";
  }, []);

  const _highestTier = useMemo(() => {
    return SEEDED_AGENTS.reduce(
      (best, agent) => (agent.reputation > best.reputation ? agent : best),
      SEEDED_AGENTS[0],
    ).tier;
  }, []);

  // -------------------------------------------------------------------------
  // Nav card live metrics
  // -------------------------------------------------------------------------

  function getNavCardMetric(cardId: string): string | null {
    if (cardId === "pools") {
      return `${String(activePools)} active pools | ${formatUsd(totalTvl)} TVL`;
    }
    if (cardId === "uniswapx") {
      return "0 pending | 0 filled";
    }
    if (cardId === "vaults") {
      return deployment?.vault ? "Deployed" : "Not deployed";
    }
    if (cardId === "agents") {
      return `${String(agentCount)} registered | ${tierBreakdown}`;
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Loading and empty states
  // -------------------------------------------------------------------------

  if (loading)
    return (
      <div className="text-text-muted flex items-center gap-2">
        <Spinner size={16} /> Loading deployment...
      </div>
    );
  if (!deployment)
    return (
      <div className="text-text-muted flex items-center gap-2">
        <Spinner size={16} /> Waiting for deployment... Run{" "}
        <code>pnpm devenv</code> to start.
      </div>
    );

  // -------------------------------------------------------------------------
  // Contract sections + counts
  // -------------------------------------------------------------------------

  const allSections = [
    deployment.foundation,
    deployment.v2,
    deployment.v3,
    deployment.v4,
    deployment.universalRouter,
    deployment.uniswapX,
    deployment.erc8004,
    ...(deployment.vault ? [deployment.vault] : []),
  ];
  const totalContracts = allSections.reduce(
    (sum, s) => sum + Object.keys(s).length,
    0,
  );

  const sections = [
    { title: "Foundation", addresses: deployment.foundation },
    { title: "Uniswap V2", addresses: deployment.v2 },
    { title: "Uniswap V3", addresses: deployment.v3 },
    { title: "Uniswap V4", addresses: deployment.v4 },
    { title: "Universal Router", addresses: deployment.universalRouter },
    { title: "UniswapX", addresses: deployment.uniswapX },
    { title: "ERC-8004", addresses: deployment.erc8004 },
    ...(deployment.vault
      ? [{ title: "Vaults", addresses: deployment.vault }]
      : []),
  ];

  // -------------------------------------------------------------------------
  // Search filter
  // -------------------------------------------------------------------------

  const filteredSections = searchQuery
    ? (sections
        .map((section) => {
          const q = searchQuery.toLowerCase();
          const filtered = Object.entries(section.addresses).filter(
            ([name, addr]) => {
              const meta = CONTRACT_DISPLAY_NAMES[name];
              return (
                name.toLowerCase().includes(q) ||
                String(addr).toLowerCase().includes(q) ||
                (meta?.display.toLowerCase().includes(q) ?? false) ||
                (meta?.description.toLowerCase().includes(q) ?? false)
              );
            },
          );
          if (filtered.length === 0) return null;
          return {
            ...section,
            addresses: Object.fromEntries(filtered),
          };
        })
        .filter(Boolean) as typeof sections)
    : sections;

  // -------------------------------------------------------------------------
  // Export helpers
  // -------------------------------------------------------------------------

  function exportAsJson() {
    const data: Record<string, Record<string, string>> = {};
    for (const section of sections) {
      const sectionData: Record<string, string> = {};
      data[section.title] = sectionData;
      for (const [name, addr] of Object.entries(section.addresses)) {
        sectionData[name] = String(addr);
      }
    }
    void copy(JSON.stringify(data, null, 2), "export-json");
    setExportOpen(false);
  }

  function exportAsEnv() {
    const lines: string[] = [];
    for (const section of sections) {
      lines.push(`# ${section.title}`);
      for (const [name, addr] of Object.entries(section.addresses)) {
        const envKey = name
          .replace(/([A-Z])/g, "_$1")
          .toUpperCase()
          .replace(/^_/, "");
        lines.push(`${envKey}=${String(addr)}`);
      }
      lines.push("");
    }
    void copy(lines.join("\n"), "export-env");
    setExportOpen(false);
  }

  // -------------------------------------------------------------------------
  // Derived display values
  // -------------------------------------------------------------------------

  const rpcUrl = "http://127.0.0.1:8545";
  const rpcDisplay = "localhost:8545";
  const gasPriceGwei = gasPrice !== null ? Number(gasPrice) / 1e9 : null;

  const deployedCount = allSections.reduce(
    (sum, s) =>
      sum +
      Object.values(s).filter(
        (v) =>
          typeof v === "string" &&
          v.startsWith("0x") &&
          v !== "0x0000000000000000000000000000000000000000",
      ).length,
    0,
  );

  const allDeployed = deployedCount === totalContracts;

  // -------------------------------------------------------------------------
  // Expand all / collapse all
  // -------------------------------------------------------------------------

  function handleToggleAll() {
    const next = !allExpanded;
    setAllExpanded(next);
    const newState: Record<string, boolean> = {};
    for (const section of sections) {
      newState[section.title] = next;
    }
    setSectionOpen(newState);
  }

  function handleSectionToggle(title: string, open: boolean) {
    setSectionOpen((prev) => ({ ...prev, [title]: open }));
    if (!open) setAllExpanded(false);
  }

  // -------------------------------------------------------------------------
  // Section preview text helper
  // -------------------------------------------------------------------------

  function getSectionPreview(addresses: Record<string, unknown>): string {
    const names = Object.keys(addresses)
      .slice(0, 3)
      .map((k) => CONTRACT_DISPLAY_NAMES[k]?.display ?? k);
    return (
      names.join(", ") + (Object.keys(addresses).length > 3 ? ", ..." : "")
    );
  }

  // -------------------------------------------------------------------------
  // Table columns for contract directory (compact single-line format)
  // -------------------------------------------------------------------------

  const columns = [
    {
      key: "name",
      label: "Contract",
      render: (value: unknown) => {
        const meta = CONTRACT_DISPLAY_NAMES[String(value)];
        const isDimmed = meta?.isContract === false;
        return (
          <div
            className={cn(
              "flex items-center gap-2 py-1",
              isDimmed && "contract-row-dimmed",
            )}
            title={meta?.description}
          >
            <span className="font-medium text-[var(--color-text)]">
              {meta?.display ?? String(value)}
            </span>
            <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
              ({String(value)})
            </span>
            {isDimmed && (
              <span className="text-text-muted rounded bg-[rgba(255,255,255,0.06)] px-1.5 py-0.5 font-mono text-[9px]">
                not a contract
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "address",
      label: "Address",
      render: (value: unknown) => <CopyableAddress address={String(value)} />,
    },
  ];

  // -------------------------------------------------------------------------
  // JSX
  // -------------------------------------------------------------------------

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_260px]">
      {/* ══════════════════════════════════════════════════════════════
       *  LEFT COLUMN — Hero metrics + navigation + contracts
       * ══════════════════════════════════════════════════════════════ */}
      <div className="min-w-0 space-y-6">
        {/* ── Hero: System + TVL ─────────────────────────────────── */}
        <PcbCard label="OVR-01" categoryLabel="PROTOCOL" cut={10}>
          {/* Title row */}
          <div className="flex items-center justify-between">
            <h1 className="font-sans text-sm font-semibold text-[var(--color-text)]">
              Overview
            </h1>
            {allDeployed ? (
              <span className="flex items-center gap-1.5 font-mono text-[10px] text-[var(--color-success)]">
                <LiveIndicator size={5} />
                {totalContracts} deployed
              </span>
            ) : (
              <span className="font-mono text-[10px] text-[var(--color-error)]">
                {deployedCount}/{totalContracts} deployed
              </span>
            )}
          </div>

          {/* System status */}
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5">
            <span className="font-mono text-[11px]">
              <span style={{ color: "var(--color-text-muted)" }}>Chain</span>{" "}
              <span style={{ color: "var(--color-text-dim)" }}>
                Anvil ({String(deployment.chainId)})
              </span>
            </span>
            <span className="font-mono text-[11px]">
              <span style={{ color: "var(--color-text-muted)" }}>Gas</span>{" "}
              <span
                className={
                  gasPriceGwei !== null ? getGasColorClass(gasPriceGwei) : ""
                }
              >
                {gasPrice !== null ? (
                  <>
                    <AnimatedBalance
                      value={gasPriceGwei ?? 0}
                      decimals={2}
                      emDash={false}
                    />{" "}
                    gwei
                  </>
                ) : (
                  "\u2014"
                )}
              </span>
            </span>
            <span className="flex items-center gap-1.5 font-mono text-[11px]">
              <span style={{ color: "var(--color-text-muted)" }}>RPC</span>{" "}
              <span style={{ color: "var(--color-text-dim)" }}>
                {rpcDisplay}
              </span>
              <CopyButton
                copied={copiedKey === "rpc-url"}
                onClick={() => copy(rpcUrl, "rpc-url")}
                fontSize={9}
              />
            </span>
            <span className="font-mono text-[11px]">
              <span style={{ color: "var(--color-text-muted)" }}>Block</span>{" "}
              <span style={{ color: "var(--color-warning)" }}>
                {blockNumber?.toString() ?? "\u2014"}
              </span>
            </span>
          </div>

          {/* Gold divider */}
          <div className="gold-divider my-5" />

          {/* TVL hero */}
          <div
            className="gold-text font-serif"
            style={{
              fontSize: "clamp(36px, 5vw, 56px)",
              lineHeight: 1.08,
            }}
          >
            {totalTvl > 0 ? formatUsd(totalTvl) : "\u2014"}
          </div>
          <div
            className="mt-2 font-mono text-[10px] uppercase"
            style={{
              letterSpacing: "0.15em",
              color: "var(--color-text-muted)",
            }}
          >
            Total Value Locked
          </div>

          {/* KV detail rows */}
          <div className="mt-5 space-y-1.5">
            <KvRow
              label="Active Pools"
              value={
                <>
                  <span style={{ color: "var(--color-info)" }}>
                    {activePools}
                  </span>
                  <span style={{ color: "var(--color-text-muted)" }}>
                    {" "}
                    / {totalPools}
                  </span>
                </>
              }
            />
            <KvRow
              label="Agents"
              value={
                <>
                  <span>{agentCount}</span>
                  <span style={{ color: "var(--color-text-muted)" }}>
                    {" "}
                    / {tierBreakdown}
                  </span>
                </>
              }
            />
          </div>
        </PcbCard>

        {/* ── Navigate ────────────────────────────────────────────── */}
        <PcbCard label="NAV-01" cut={10}>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
            {NAV_CARDS.map((card) => {
              const Icon = card.icon;
              const metric = getNavCardMetric(card.id);
              return (
                <button
                  type="button"
                  key={card.id}
                  className="group relative flex flex-col gap-1.5 rounded-sm p-3 text-left transition-all duration-300 hover:bg-[rgba(201,168,76,0.04)]"
                  onClick={() => navigate(card.path)}
                >
                  <div className="flex items-center gap-2">
                    <Icon
                      size={14}
                      className="flex-shrink-0 opacity-40 transition-opacity duration-300 group-hover:opacity-80"
                      style={{ color: "var(--color-gold)" }}
                    />
                    <span
                      className="font-mono text-[11px] transition-colors duration-300 group-hover:text-[var(--color-text)]"
                      style={{ color: "var(--color-text-dim)" }}
                    >
                      {card.label}
                    </span>
                    <ChevronRight
                      size={10}
                      className="ml-auto flex-shrink-0 opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-50"
                      style={{ color: "var(--color-text-muted)" }}
                    />
                  </div>
                  {metric && (
                    <div
                      className="flex items-center gap-1.5 font-mono text-[9px]"
                      style={{ color: "var(--color-text-muted)" }}
                    >
                      <LiveIndicator size={3} />
                      {metric}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </PcbCard>

        {/* ── Contract Registry ────────────────────────────────────── */}
        <PcbCard label="REG-01" categoryLabel="CONTRACTS" cut={10}>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-sans text-sm font-semibold text-[var(--color-text)]">
              Contract Registry
            </h3>
            <span
              className="font-mono text-[10px]"
              style={{ color: "var(--color-text-muted)" }}
            >
              {totalContracts} registered
            </span>
          </div>

          {/* Controls row */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-[200px] flex-1">
              <input
                type="text"
                placeholder="Search by name or address..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                className="search-input w-full px-3 py-2 font-mono text-[12px] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[rgba(201,168,76,0.4)] focus:outline-none"
              />
            </div>
            <button
              type="button"
              className="flex items-center gap-1.5 border border-[rgba(42,48,64,0.5)] bg-[rgba(13,17,23,0.6)] px-3 py-2 font-mono text-[11px] text-[var(--color-text-dim)] transition-colors hover:border-[rgba(201,168,76,0.3)] hover:text-[var(--color-text)]"
              onClick={handleToggleAll}
            >
              <ChevronsUpDown size={12} />
              {allExpanded ? "Collapse" : "Expand"}
            </button>
            <div className="relative">
              <button
                type="button"
                className="flex items-center gap-1.5 border border-[rgba(42,48,64,0.5)] bg-[rgba(13,17,23,0.6)] px-3 py-2 font-mono text-[11px] text-[var(--color-text-dim)] transition-colors hover:border-[rgba(201,168,76,0.3)] hover:text-[var(--color-text)]"
                onClick={() => {
                  setExportOpen((v) => !v);
                }}
              >
                <ChevronDown size={12} />
                Export
              </button>
              {exportOpen && (
                <div
                  className="absolute right-0 top-full z-10 mt-1 overflow-hidden border"
                  style={{
                    borderColor: "rgba(201, 168, 76, 0.18)",
                    background: "rgba(13, 17, 23, 0.95)",
                    boxShadow: "4px 4px 0 rgba(0, 0, 0, 0.3)",
                  }}
                >
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-center gap-1.5 px-4 py-2.5 font-mono text-[11px] transition-colors hover:bg-[rgba(201,168,76,0.04)]",
                      copiedKey === "export-json"
                        ? "text-[var(--color-success)]"
                        : "text-[var(--color-text-dim)]",
                    )}
                    onClick={exportAsJson}
                  >
                    <Copy size={12} />
                    {copiedKey === "export-json" ? "Copied!" : "Copy JSON"}
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-center gap-1.5 px-4 py-2.5 font-mono text-[11px] transition-colors hover:bg-[rgba(201,168,76,0.04)]",
                      copiedKey === "export-env"
                        ? "text-[var(--color-success)]"
                        : "text-[var(--color-text-dim)]",
                    )}
                    onClick={exportAsEnv}
                  >
                    <FileText size={12} />
                    {copiedKey === "export-env" ? "Copied!" : "Copy .env"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sections */}
          <div className="mt-4 space-y-3">
            {filteredSections.map((section) => {
              const config = SECTION_CONFIG[section.title];
              const entryCount = Object.keys(section.addresses).length;
              const previewText = getSectionPreview(
                section.addresses as unknown as Record<string, unknown>,
              );

              return (
                <CollapsibleSection
                  key={section.title}
                  title={
                    <span className="flex items-center gap-2">
                      {config?.protocol && (
                        <ProtocolBadge protocol={config.protocol} />
                      )}
                      <span>{section.title}</span>
                    </span>
                  }
                  defaultOpen={false}
                  open={sectionOpen[section.title]}
                  onToggle={(open) => {
                    handleSectionToggle(section.title, open);
                  }}
                  badge={String(entryCount)}
                  borderColor={config?.color}
                  previewText={previewText}
                >
                  <Table
                    columns={columns}
                    data={Object.entries(section.addresses).map(
                      ([name, addr]) => ({
                        name,
                        address: String(addr),
                      }),
                    )}
                    emptyText="No addresses"
                  />
                </CollapsibleSection>
              );
            })}
          </div>
        </PcbCard>
      </div>

      {/* ══════════════════════════════════════════════════════════════
       *  RIGHT COLUMN — Stat cards with hex connectors (vertical spine)
       * ══════════════════════════════════════════════════════════════ */}
      <div className="hidden flex-col items-center lg:flex">
        <PcbCard
          label="GTS-A01"
          categoryLabel="POOLS"
          cut={8}
          className="w-full [&_.pcb-card-inner]:p-5"
        >
          <StatCard
            value={String(activePools)}
            label="Active Pools"
            variant="ghost"
          />
          <div
            className="mt-1 font-mono text-[9px]"
            style={{ color: "var(--color-text-muted)" }}
          >
            of {totalPools} total
          </div>
        </PcbCard>

        <HexConnector />

        <PcbCard
          label="GTS-S01"
          categoryLabel="AGENTS"
          cut={8}
          className="w-full [&_.pcb-card-inner]:p-5"
        >
          <StatCard
            value={String(agentCount)}
            label="Registered Agents"
            variant="ghost"
          />
          <div
            className="mt-1 font-mono text-[9px]"
            style={{ color: "var(--color-text-muted)" }}
          >
            {tierBreakdown}
          </div>
        </PcbCard>

        <HexConnector delay={0.15} />

        <PcbCard
          label="GTS-B01"
          categoryLabel="CHAIN"
          cut={8}
          className="w-full [&_.pcb-card-inner]:p-5"
        >
          <StatCard
            value={blockNumber?.toString() ?? "\u2014"}
            label="Block Height"
            variant="ghost"
          />
        </PcbCard>

        <HexConnector delay={0.3} />

        <PcbCard
          label="GTS-G01"
          categoryLabel="GAS"
          cut={8}
          className="w-full [&_.pcb-card-inner]:p-5"
        >
          <StatCard
            value={gasPriceGwei !== null ? gasPriceGwei.toFixed(2) : "\u2014"}
            label="Gas (gwei)"
            variant="ghost"
          />
        </PcbCard>
      </div>
    </div>
  );
}
